import React, { useState, useEffect } from 'react';
import { Modal, Select, Button, message, InputNumber } from 'antd';
import requirementApi from '@/fetch/bussiness/collectionTask';
import { HttpStatusCode } from '@/fetch/core/constant';
import { PLACEHOLDER_TAG_ID } from '../CreateRequirement';
import './EditTagModal.scss';

interface EditTagModalProps {
  visible: boolean;
  title?: string;
  tagName: string;
  currentTagId?: number;
  tagOptionList?: Array<{ value: string | number; label: string }>;
  modalType?:
    | 'addRelated'
    | 'addDetail'
    | 'editMain'
    | 'editRelated'
    | 'editDetail';
  parentTagName?: string;
  nodeLevel?: number;
  currentSceneCount?: number;
  onClose: () => void;
  onSave: (
    oldTagId: number,
    newTagData: { tagId: number; tagName: string; sceneCount?: number },
    isRootNodeEdit?: boolean
  ) => void;
  onAddTag?: (tagData: { tagId: number; tagName: string }) => void;
  onConfirm?: (data: {
    tagId: number;
    tagName: string;
    sceneCount?: number;
    tagType?: string;
  }) => void;
  onRefreshTagList?: () => Promise<void>; // 新增：刷新标签列表的回调
}

const EditTagModal: React.FC<EditTagModalProps> = ({
  visible,
  title = '编辑主标签',
  tagName,
  currentTagId,
  modalType,
  parentTagName,
  nodeLevel = 0,
  currentSceneCount,
  tagOptionList,
  onClose,
  onSave,
  onAddTag,
  onConfirm,
  onRefreshTagList,
}) => {
  const [selectedValue, setSelectedValue] = useState<number | undefined>(
    undefined
  );
  const [searchValue, setSearchValue] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [sceneCount, setSceneCount] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (visible) {
      // 如果有currentTagId，设置为选中状态
      if (currentTagId && currentTagId !== PLACEHOLDER_TAG_ID) {
        setSelectedValue(currentTagId);
      } else {
        // 只有在modal刚打开时才重置selectedValue，避免覆盖新创建标签的选中状态
        setSelectedValue(undefined);
      }
      setSearchValue('');
      // 初始化场景数量
      if (modalType === 'editRelated' && currentSceneCount !== undefined) {
        setSceneCount(currentSceneCount);
      } else {
        setSceneCount(undefined);
      }
    } else {
      // modal关闭时重置状态
      setSelectedValue(undefined);
      setSearchValue('');
      setSceneCount(undefined);
    }
  }, [visible, currentTagId, modalType, currentSceneCount]);

  // 校验采集场景数（非必填）
  const validateSceneCount = (count: number | undefined): boolean => {
    // 如果为空，则跳过校验（非必填）
    if (count === undefined || count === null || count == 0) {
      return true;
    }

    if (!Number.isInteger(count)) {
      message.error('采集场景数必须是整数');
      return false;
    }
    if (count < 1 || count > 99999) {
      message.error('采集场景数必须在1-99999之间');
      return false;
    }
    return true;
  };

  const handleSave = () => {
    if (modalType === 'addRelated') {
      // 添加关联标签模式
      if (selectedValue && onConfirm) {
        // 校验采集场景数
        if (!validateSceneCount(sceneCount)) {
          return; // 校验失败，不关闭弹窗
        }

        if (typeof selectedValue === 'number') {
          const selectedOption = tagOptionList?.find(
            (option) => option.value === selectedValue
          );
          if (selectedOption) {
            onConfirm({
              tagId: selectedOption.value as number,
              tagName: selectedOption.label,
              tagType: 'CHILDREN', // 关联标签类型
              sceneCount: sceneCount || 0,
            });
          }
        }
        onClose();
      } else {
        message.warning('请选择一个标签');
      }
    } else if (modalType === 'addDetail') {
      // 添加细化标签模式
      if (selectedValue && onConfirm) {
        if (typeof selectedValue === 'number') {
          const selectedOption = tagOptionList?.find(
            (option) => option.value === selectedValue
          );
          if (selectedOption) {
            onConfirm({
              tagId: selectedOption.value as number,
              tagName: selectedOption.label,
              tagType: 'DETAIL', // 细化标签类型
              // 细化标签不需要场景数
            });
          }
        }
        onClose();
      } else {
        message.warning('请选择一个标签');
      }
    } else if (
      modalType === 'editMain' ||
      modalType === 'editRelated' ||
      modalType === 'editDetail'
    ) {
      // 编辑标签模式
      if (selectedValue && currentTagId) {
        // 如果是编辑关联标签，需要校验采集场景数
        if (modalType === 'editRelated' && !validateSceneCount(sceneCount)) {
          return; // 校验失败，不关闭弹窗
        }

        const selectedOption = tagOptionList?.find(
          (option) => option.value === selectedValue
        );

        if (selectedOption) {
          onSave(
            currentTagId,
            {
              tagId: selectedOption.value as number,
              tagName: selectedOption.label,
              ...(modalType === 'editRelated' && { sceneCount }),
            },
            modalType === 'editMain'
          );
        }
        onClose();
      }
    }
  };

  const handleSelectChange = (value: number) => {
    setSelectedValue(value);
  };

  const handleSearch = (value: string) => {
    if (value?.length > 30) {
      return;
    }
    setSearchValue(value?.trim());
  };

  // 高亮匹配文本的函数
  const highlightText = (text: string, searchText: string) => {
    if (!searchText) return text;

    const regex = new RegExp(`(${searchText})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <span key={index} className="search-highlight">
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  return (
    <Modal
      title={title}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={600}
      className="edit-tag-modal"
      destroyOnHidden
    >
      <div className="modal-content">
        <div className="input-section inline-layout">
          <div className="input-label">搜索或新建标签</div>
          <Select
            value={selectedValue}
            onChange={handleSelectChange}
            onSearch={handleSearch}
            placeholder="请选择标签"
            className="tag-select"
            showSearch
            allowClear
            searchValue={searchValue}
            open={dropdownOpen}
            onDropdownVisibleChange={setDropdownOpen}
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            options={tagOptionList}
            optionRender={(option) => (
              <div className="custom-option">
                <span className="option-text">
                  {highlightText(String(option.label || ''), searchValue)}
                </span>
              </div>
            )}
            dropdownRender={(menu) => (
              <div>
                {searchValue &&
                  !tagOptionList?.some((option) =>
                    option.label
                      .toLowerCase()
                      .includes(searchValue.toLowerCase())
                  ) && (
                    <div
                      className="create-new-option"
                      onClick={async () => {
                        setDropdownOpen(false);
                        try {
                          const res = await requirementApi.addTag(searchValue);
                          if (res?.code === HttpStatusCode.Success) {
                            const newTagId = res?.data?.tagId;
                            const newTagName = res?.data?.tagName;
                            // 先刷新标签列表数据
                            if (onRefreshTagList) {
                              await onRefreshTagList();
                            }

                            // 数据刷新完成后，设置新创建的标签为选中状态
                            const timer = setTimeout(() => {
                              clearTimeout(timer);
                              setSelectedValue(newTagId);
                            }, 100);
                            setSearchValue('');
                            let tagType = 'MAIN'; // 默认值
                            if (modalType === 'addRelated') {
                              tagType = 'CHILDREN'; // 关联标签
                            } else if (modalType === 'addDetail') {
                              tagType = 'DETAIL'; // 细化标签
                            }

                            const newTagData = {
                              tagId: newTagId,
                              tagName: newTagName,
                              tagType: tagType,
                            };

                            if (onAddTag) {
                              onAddTag(newTagData);
                            }
                            message.success(`成功新建标签 "${searchValue}"`);
                          } else {
                            message.error('标签创建失败');
                          }
                        } catch (error) {}
                      }}
                    >
                      <i style={{ whiteSpace: 'nowrap' }}>点击新建「</i>
                      <span className="highlight">{searchValue}</span>
                      <i style={{ whiteSpace: 'nowrap' }}>」标签</i>
                    </div>
                  )}
                {menu}
              </div>
            )}
          />
        </div>

        {(modalType === 'addRelated' || modalType === 'editRelated') && (
          <div className="input-section inline-layout">
            <div className="input-label">采集场景数</div>
            <InputNumber
              value={sceneCount || undefined}
              onChange={(value) => setSceneCount(value || undefined)}
              placeholder="请输入1-99999的整数（可选）"
              className="scene-count-input"
              min={1}
              max={99999}
              precision={0}
              style={{ width: '100%' }}
              controls={false}
            />
          </div>
        )}

        <div className="modal-footer single-button">
          <Button
            type="primary"
            onClick={handleSave}
            className="confirm-btn"
            disabled={!selectedValue}
          >
            确定
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default EditTagModal;
